import { useMemo } from 'react';

import { commonApi } from '@internal/api/common';
import { Profile } from '@internal/types/profile';
import { Project, ProjectQuestion } from '@internal/types/projects';
import { generateOptionsWith30MinIntervals } from '@internal/utils/date';
import { prepareTimezones } from '@internal/utils/timezones';

import { getQuestionsToDisplay } from './apply-form.utils';

const rateToString = (rate?: number | null): string =>
  rate ? String(rate) : '';

type UseQuestionsParams = {
  questions: ProjectQuestion[];
  project: Project;
  profile?: Profile | null;
};

type UseQuestionsReturnType<FormValues> = {
  defaultValues: FormValues;
  questionTitles: Array<string>;
};

export const useQuestions = <FormValues>({
  questions,
  project,
  profile,
}: UseQuestionsParams): UseQuestionsReturnType<FormValues> => {
  const questionsToDisplay = useMemo(
    () =>
      getQuestionsToDisplay({
        questions,
        answers: project.projectAnswers || [],
        isApplicationFormSubmitted: project.hasCandidateConfirmedApplication,
      }),
    [
      questions,
      project.projectAnswers,
      project.hasCandidateConfirmedApplication,
    ]
  );

  const defaultValues = useMemo(() => {
    const isPartnerManager = profile?.role === 'partner manager';
    const developerToPrefillData = isPartnerManager
      ? project.partnerDeveloper
      : profile;
    const rateSource = isPartnerManager ? null : profile;

    return {
      basicDetails: {
        preferredStartDate:
          project.readyToStartAt ||
          developerToPrefillData?.readyToStartWhen ||
          '',
        minRate: rateToString(
          project.desiredRateMin ?? rateSource?.min_rate_per_hour
        ),
        maxRate: rateToString(
          project.desiredRateMax ?? rateSource?.max_rate_per_hour
        ),
        rateComment: project.candidateRateComment || '',
        vacationPlans:
          project.vacationPlans || developerToPrefillData?.vacationPlans || '',
        timeslots:
          project.interviewTimeslots ||
          developerToPrefillData?.timeSlotsForInterview ||
          '',
      },
      workingHours: {
        startTime:
          project.workingHoursFrom ||
          developerToPrefillData?.workingHoursFrom ||
          '',
        endTime:
          project.workingHoursTo ||
          developerToPrefillData?.workingHoursTo ||
          '',
        timeZone: project.timeZone || developerToPrefillData?.timeZone || null,
      },
      additionalComments: project.candidateApplicationComment || '',
      questions: questionsToDisplay.map(({ id }) => ({
        id,
        answer:
          project.projectAnswers?.find(
            projectAnswer => projectAnswer.hubspotProjectQuestionId === id
          )?.text || '',
      })),
      agree: false,
    } as FormValues;
  }, [project, questionsToDisplay, profile]);

  /**
   * Instead of a Map structure, we use array indexing because it is used along with
   * `useFieldArray`. This ensures the time complexity is O(1).
   */
  const questionTitles = useMemo(() => {
    return questionsToDisplay.map(({ title }) => title) || [];
  }, [questionsToDisplay]);

  return { defaultValues, questionTitles };
};

type UseOptionsReturnType = {
  timeOptions: Array<{ label: string; value: string }>;
  timeZoneOptions: Array<{ label: string; value: string }>;
};

export const useOptions = (): UseOptionsReturnType => {
  const { constants } = commonApi.getConstants.useQuery();

  const timeOptions = useMemo(() => generateOptionsWith30MinIntervals(), []);

  const timeZoneOptions = useMemo(
    () => prepareTimezones(constants?.timeZones || []),
    [constants]
  );

  return { timeOptions, timeZoneOptions };
};
